import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { useGlobalMenu, GlobalMenuState } from '../../contexts/GlobalMenuStore';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';
import { PALWidget, PersonalAccessLibrary } from './PersonalAccessLibrary/PersonalAccessLibrary';
import { isNonEmptyBlock } from '../../types/api/Block';

// Components

export type PersonalSectionProps = {
  uuid: string;
  widgets?: PALWidget[];
};

export function PersonalSection({ widgets = [] }: PersonalSectionProps) {
  const personalBoxRef = useRef<HTMLDivElement>(null);
  const { userInfo } = useAppContext();
  const { currentOpenMenu, closeMenus, togglePAL } = useGlobalMenu();
  const [transitionEnded, setTransitionEnded] = useState<boolean>(false);

  const isExpanded = currentOpenMenu === GlobalMenuState.PAL;

  // filter all empty widgets/blocks -> we don't want to show pal tab with empty content
  const nonEmptyWidgets = widgets.filter(widget => widget.blocks.some(isNonEmptyBlock));

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (personalBoxRef.current === null) {
        return;
      }
      const isInPersonalSection = personalBoxRef.current.contains(event.target as Node);
      const isClickedTargetInDOM = document.body.contains(event.target as Node);
      const isClickInsideModal = document.getElementById(GlobalDocumentIds.modalDialog)?.contains(event.target as Node);
      const isClickOnSkipLink = (event.target as HTMLAnchorElement)?.hash === `#${GlobalDocumentIds.personal}`;

      if (!isInPersonalSection && isClickedTargetInDOM && isExpanded && !isClickInsideModal && !isClickOnSkipLink) {
        closeMenus();
      }
    };

    if (isExpanded) {
      document.addEventListener('click', onClickOutside);
    }
    return () => document.removeEventListener('click', onClickOutside);
  }, [isExpanded, closeMenus]);

  return useMemo(
    () => (
      <div
        role="region"
        aria-labelledby="personal-access-library-header" // find this in <PersonalAccessLibrary />
        id={GlobalDocumentIds.personalBox}
        ref={personalBoxRef}
        className={isExpanded ? 'wrapper__personal wrapper__personal--expanded' : 'wrapper__personal'}
        onTransitionEnd={() => {
          // integrating transition into render lifecycle (SIBZIEL-6901)
          setTransitionEnded(isExpanded);
        }}
      >
        <section className="personal section" id={GlobalDocumentIds.personal}>
          {nonEmptyWidgets.length > 0 && (
            <PersonalAccessLibrary
              accessKeysEnabled={userInfo?.settings.accessKeysEnabled || false}
              togglePAL={togglePAL}
              isExpanded={isExpanded}
              transitionEnded={transitionEnded}
              widgets={nonEmptyWidgets}
            />
          )}
        </section>
      </div>
    ),
    [isExpanded, nonEmptyWidgets, togglePAL, transitionEnded, userInfo?.settings.accessKeysEnabled]
  );
}
